import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useCart } from '@/store/cart';
import { useSession } from '@/lib/auth';
import {
  initiatePayment,
  submitStubWebhook,
  getOrder,
  CheckoutClientError,
  type InitiatePaymentResponse,
  type SubmitStubWebhookResponse,
} from '@/lib/checkoutClient';
import { stubAdapter } from '@/lib/payment/adapters/StubAdapter';
import { useOrderPolling, isTerminalOrderStatus } from '@/hooks/useOrderPolling';
import { messageForCode } from '@/lib/checkout/messages';
import { Badge, Button, Card, CardTitle, toast } from '@/components/ui';
import { StubPaymentForm, type StubFormValues } from '@/components/payment/StubPaymentForm';
import { MembershipCheckout } from '@/components/checkout/MembershipCheckout';
import { InstallmentSelector } from '@/components/checkout/InstallmentSelector';
import { calculateInstallmentPlan } from '@/lib/checkout/installments';
import { formatCOP, formatEventDate } from '@/lib/format';

// =============================================================================
// ROUND7 pre-Ola 2 — Checkout flag-mode dispatch
// =============================================================================
// Default 'event' preserva todo el comportamiento previo (event ticket flow
// con cart.lock guard + /cart/lock + stub adapter + order polling).
// 'membership' / 'experience' modes son placeholders para Ola 2 — los
// sub-agents commerce-checkout-agent (P2 Memberships) y commerce-ui-agent
// (P3 Experiences) extienden las branches específicas para crear las cart
// lines, copies, y dispatch correcto por mode.

export type CheckoutMode = 'event' | 'membership' | 'experience' | 'product' | 'flash';

function isCheckoutMode(value: string | null): value is CheckoutMode {
  return (
    value === 'event' ||
    value === 'membership' ||
    value === 'experience' ||
    value === 'product' ||
    value === 'flash'
  );
}

type ModeCopy = {
  pageTitle: string;
  summaryTitle: string;
  pendingDescription: string;
  redirectOnNoState: string;
  redirectLabel: string;
};

const MODE_COPY: Record<CheckoutMode, ModeCopy> = {
  event: {
    pageTitle: 'Pagar',
    summaryTitle: 'Reserva confirmada',
    pendingDescription:
      'Estamos confirmando tu pago con el procesador. Esto suele tomar unos segundos.',
    redirectOnNoState: '/cart',
    redirectLabel: 'Volver al carrito',
  },
  membership: {
    pageTitle: 'Suscribirme a la membresía',
    summaryTitle: 'Plan seleccionado',
    pendingDescription: 'Estamos procesando tu suscripción.',
    redirectOnNoState: '/memberships',
    redirectLabel: 'Volver a membresías',
  },
  experience: {
    pageTitle: 'Reservar experiencia',
    summaryTitle: 'Experiencia seleccionada',
    pendingDescription: 'Estamos procesando tu reserva.',
    redirectOnNoState: '/experiences',
    redirectLabel: 'Volver a experiencias',
  },
  product: {
    pageTitle: 'Pago de productos partners',
    summaryTitle: 'Productos seleccionados',
    pendingDescription: 'Estamos procesando tu pedido.',
    redirectOnNoState: '/cart',
    redirectLabel: 'Volver al carrito',
  },
  // Flash sales (C3): el fan llega acá con turno vigente (`?turno=1` en el
  // detalle → CTA "Ir a pagar"). El flujo real es Ola 2; hoy cae en el
  // placeholder de abajo en vez de rebotar a `/cart` como haría `event`.
  flash: {
    pageTitle: 'Finalizar compra',
    summaryTitle: 'Promo flash seleccionada',
    pendingDescription: 'Estamos procesando tu compra.',
    redirectOnNoState: '/flash-sales',
    redirectLabel: 'Volver a las promos flash',
  },
};

/**
 * Checkout — Click 3. TASK-C07 wires the payment form against the stub
 * adapter + webhook + order polling.
 *
 * Flow on submit:
 *   1. POST /payment/initiate → paymentId + externalReference + flow='stub_immediate'
 *   2. Use `stubAdapter.initiate()` locally to compose the webhook payload
 *      (this gives us the simulated_outcome controlled by the last char of
 *      the idempotency-key — ADR-0003).
 *   3. POST /payment/webhook/stub with that payload
 *   4. Start polling /order/:id (useOrderPolling) until status terminal
 *   5. On confirmed → navigate /checkout/success?order_id=X
 *   6. On failed → toast + stay on form for retry (new idempotency-key?)
 *
 * Guards on mount: redirect /cart if no lock, redirect / if lock expired.
 */
/**
 * Checkout — dispatcher por `mode` (event / membership / experience /
 * product). Sólo decide qué pantalla renderizar; los hooks del flujo de
 * evento viven en `EventCheckout` para no llamarse condicionalmente.
 */
export function Checkout(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get('mode');
  const mode: CheckoutMode = isCheckoutMode(rawMode) ? rawMode : 'event';
  const copy = MODE_COPY[mode];

  // ROUND12 — branch real para mode=membership.
  if (mode === 'membership') {
    // ROUND17 #10: aceptar `?tier=<slug>` (link interno) o `?plan_slug=<slug>`
    // (redirect cross-platform desde web/app).
    const tierSlug = searchParams.get('plan_slug') ?? searchParams.get('tier') ?? '';
    const cycleParam = searchParams.get('cycle');
    const cycle: 'monthly' | 'annual' = cycleParam === 'annual' ? 'annual' : 'monthly';
    if (!tierSlug) {
      return (
        <section className="container-fc py-12">
          <Card variant="outlined" padded>
            <CardTitle>Plan no especificado</CardTitle>
            <p className="mt-3 text-fc-muted">
              Volvé a elegir tu membresía para continuar con el pago.
            </p>
            <Button className="mt-6" variant="secondary" onClick={() => navigate('/memberships')}>
              Ver membresías
            </Button>
          </Card>
        </section>
      );
    }
    return <MembershipCheckout tierSlug={tierSlug} cycle={cycle} />;
  }

  // Placeholder para experience / product hasta que sus flujos se conecten.
  if (mode !== 'event') {
    return (
      <section className="container-fc py-12">
        <header className="mb-6">
          <h1 className="font-display text-3xl md:text-4xl">{copy.pageTitle}</h1>
        </header>
        <Card variant="outlined" padded>
          <CardTitle>{copy.summaryTitle}</CardTitle>
          <p className="mt-3 text-fc-muted">
            El flujo de checkout para <strong>{mode}</strong> entra en la siguiente entrega (Ola 2).
            Mientras tanto podés volver a la sección y elegir otra opción.
          </p>
          <Button
            className="mt-6"
            variant="secondary"
            onClick={() => navigate(copy.redirectOnNoState)}
          >
            {copy.redirectLabel}
          </Button>
        </Card>
      </section>
    );
  }

  return <EventCheckout copy={copy} />;
}

/**
 * EventCheckout — flujo de pago de boletas (mode='event'). Aquí viven todos
 * los hooks del checkout, llamados siempre en el mismo orden.
 */
function EventCheckout({ copy }: { copy: ModeCopy }): JSX.Element {
  const navigate = useNavigate();
  const cart = useCart();
  const { user } = useSession();
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [waitingOnOrder, setWaitingOnOrder] = useState<string | null>(null);
  // Cuotas — estado elevado al padre: el resumen de cobro deriva de él, y el día
  // que la pasarela real acepte diferido, este número viaja en el POST de pago.
  const [installments, setInstallments] = useState(1);

  // Auth + lock guards.
  useEffect(() => {
    if (!cart.lock) {
      navigate('/cart', { replace: true });
      return;
    }
    if (cart.isLockExpired()) {
      toast.info({ title: 'Reserva expirada', description: 'Volvé a elegir tus localidades.' });
      cart.reset();
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Total real de la orden: el checkout no lo tenía en el cliente; se trae de
  // GET /order/:id. `totalCents` del contrato viene en PESOS ENTEROS (pese al
  // nombre — `centsToWholeCop` ya dividió /100), así que para el InstallmentSelector,
  // que trabaja en CENTAVOS, se multiplica ×100.
  const orderQuery = useQuery({
    queryKey: ['checkout-order-total', cart.lock?.orderId],
    queryFn: () => getOrder(cart.lock!.orderId),
    enabled: !!cart.lock?.orderId,
    staleTime: 30_000,
  });
  const totalWholeCop = orderQuery.data?.totalCents ?? 0;
  const totalCents = totalWholeCop * 100;
  const plan = calculateInstallmentPlan(totalCents, installments);

  // Two mutations chained: initiate → submitWebhook.
  const initiateMut = useMutation<InitiatePaymentResponse, CheckoutClientError, void>({
    mutationFn: async () => {
      if (!cart.lock) throw new CheckoutClientError('ORDER_INVALID_STATE', 'No lock', 409);
      if (!cart.idempotencyKey)
        throw new CheckoutClientError('VALIDATION_FAILED', 'Missing idempotency key', 422);
      return initiatePayment({
        orderId: cart.lock.orderId,
        providerSlug: import.meta.env.VITE_PAYMENT_PROVIDER_SLUG ?? 'stub',
        idempotencyKey: cart.idempotencyKey,
      });
    },
  });

  const webhookMut = useMutation<
    SubmitStubWebhookResponse,
    CheckoutClientError,
    InitiatePaymentResponse
  >({
    // The initiated payment is passed in as the mutation variable (from
    // handleSubmit's `init`), NOT read from `initiateMut.data`. Right after
    // `initiateMut.mutateAsync()` resolves, React has not re-rendered yet, so
    // `initiateMut.data` is still stale (undefined on the first attempt) — that
    // race made this step throw VALIDATION_FAILED and the stub confirmation
    // never ran, leaving the order stuck in payment_pending.
    mutationFn: async (initiated: InitiatePaymentResponse) => {
      if (!cart.lock || !cart.idempotencyKey) {
        throw new CheckoutClientError('VALIDATION_FAILED', 'Missing payment context', 422);
      }
      // The StubAdapter computes the simulated outcome locally and builds the
      // webhook payload the way the server expects.
      const adapterResult = await stubAdapter.initiate({
        orderId: cart.lock.orderId,
        amount: { amount_cents: 0, currency: 'COP' }, // server is source of truth for amount
        idempotencyKey: cart.idempotencyKey,
        customerEmail: user?.email ?? 'unknown@local',
      });
      if (!adapterResult.stubWebhookPayload) {
        throw new CheckoutClientError(
          'VALIDATION_FAILED',
          'Stub adapter did not produce a webhook payload',
          422,
        );
      }
      return submitStubWebhook({
        paymentId: initiated.paymentId,
        externalReference: initiated.externalReference,
        payload: adapterResult.stubWebhookPayload,
      });
    },
  });

  async function handleSubmit(_values: StubFormValues): Promise<void> {
    void _values; // card number etc. is ignored by stub; kept for E2E shape parity
    setPaymentInitiated(true);
    try {
      const init = await initiateMut.mutateAsync();
      const result = await webhookMut.mutateAsync(init);
      if (!result.processed && !cart.lock) {
        toast.error({
          title: 'Pago no procesado',
          description: messageForCode('VALIDATION_FAILED'),
        });
        setPaymentInitiated(false);
        return;
      }
      // Whether processed:true (new) or processed:false (replay), the order
      // now has a definitive status. We start polling to wait for terminal.
      setWaitingOnOrder(init.paymentId ? (cart.lock?.orderId ?? null) : null);
    } catch (e) {
      const err = e as CheckoutClientError;
      const msg = messageForCode(err.code, err.status);
      toast.error({ title: 'Pago rechazado', description: msg });
      setPaymentInitiated(false);
      // Don't auto-reset idempotency-key — same key + retry = same outcome.
      // User has to consciously "Probar de nuevo" to regenerate.
    }
  }

  function handleRetry(): void {
    // Regenerate the idempotency-key so a new attempt creates a NEW payment.
    cart.finalizeCheckout(); // wipes idem key + lock
    toast.info({ title: 'Reiniciá la compra', description: 'Volvé a elegir las localidades.' });
    navigate('/', { replace: true });
  }

  // Poll the order while we're waiting on webhook confirmation.
  const orderPoll = useOrderPolling(waitingOnOrder ?? undefined, {
    enabled: !!waitingOnOrder,
    intervalMs: 2_500, // a bit tighter than the 5s inventory polling — payment is hot path
  });

  useEffect(() => {
    if (!orderPoll.data) return;
    const status = orderPoll.data.status;
    if (!isTerminalOrderStatus(status)) return;
    if (status === 'confirmed' || status === 'qr_issued' || status === 'used') {
      toast.success({ title: 'Pago confirmado', description: 'Tu QR ya está listo.' });
      const orderId = orderPoll.data.id;
      // Don't finalize the cart here — CheckoutSuccess does it after rendering
      // the QR so the user has a stable surface.
      navigate(`/checkout/success?order_id=${encodeURIComponent(orderId)}`);
    } else {
      toast.error({ title: 'Pago no confirmado', description: messageForCode('PAYMENT_DECLINED') });
      setPaymentInitiated(false);
      setWaitingOnOrder(null);
    }
  }, [orderPoll.data, navigate]);

  if (!cart.lock) return <></>;

  const expiresIn = Math.max(0, new Date(cart.lock.expiresAt).getTime() - Date.now());
  const expiresMin = Math.floor(expiresIn / 60_000);
  const pending =
    paymentInitiated ||
    initiateMut.isPending ||
    webhookMut.isPending ||
    (orderPoll.isFetching && !!waitingOnOrder);

  return (
    <section className="container-fc py-8 md:py-12">
      <header className="mb-6">
        <h1 className="font-display text-3xl md:text-4xl">{copy.pageTitle}</h1>
        <p className="mt-1 text-fc-muted">
          Tu reserva está activa por {expiresMin} minuto{expiresMin !== 1 ? 's' : ''} más.
        </p>
      </header>

      <Card variant="default" padded>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>{copy.summaryTitle}</CardTitle>
            <p className="mt-1 text-sm text-fc-muted">
              Order <code className="font-mono text-fc-ink">{cart.lock.orderId.slice(0, 8)}</code>
            </p>
            <p className="mt-2 text-sm text-fc-muted">
              Expira{' '}
              <time dateTime={cart.lock.expiresAt}>{formatEventDate(cart.lock.expiresAt)}</time>
            </p>
          </div>
          <Badge tone={waitingOnOrder ? 'info' : 'success'}>
            {waitingOnOrder ? 'Esperando confirmación' : 'Reservado'}
          </Badge>
        </div>

        {/* Total real de la orden — el resumen de cobro. Se refleja el desglose
            de cuotas debajo cuando el fan difiere el pago. */}
        {totalCents > 0 && (
          <div className="mt-4 flex items-center justify-between border-t border-fc-line pt-4">
            <span className="text-sm text-fc-muted">Total a pagar</span>
            <span className="font-display text-2xl tabular-nums text-fc-ink">
              {formatCOP(totalWholeCop)}
            </span>
          </div>
        )}

        {/* Orden del flujo de pago: input de tarjeta → cuotas → desglose → botón
            "Pagar" (último). El selector y el desglose van como children del form,
            entre el input y el botón, para que el CTA cierre el flujo. */}
        <div className="mt-6">
          <StubPaymentForm
            defaultEmail={user?.email ?? ''}
            collectUserInfo={false /* Q9: server reads from auth.users */}
            disabled={pending}
            pending={pending}
            onSubmit={handleSubmit}
          >
            {/* Cuotas — el StubPaymentForm ES la tarjeta de crédito (único método
                hoy), así que el diferido aplica directo. La isla usa su propio DS
                (Sora + Mi Blue) sin tocar el resto del checkout (Inter/Poppins). */}
            {totalCents > 0 && (
              <InstallmentSelector
                totalCents={totalCents}
                value={installments}
                onChange={setInstallments}
              />
            )}

            {/* Desglose reflejado en el resumen de cobro, derivado del estado
                elevado. `formatCOP` (pesos enteros) para ser consistente con
                "Total a pagar"; `plan.*Cents` está en centavos → /100. */}
            {plan.isDeferred && (
              <div className="rounded-card border border-fc-line bg-fc-paper p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-fc-muted">Se cobrará en {installments} cuotas de</span>
                  <span className="font-semibold tabular-nums text-fc-ink">
                    {formatCOP(Math.round(plan.perInstallmentCents / 100))}
                  </span>
                </div>
                <p className="mt-1 text-xs text-fc-muted">
                  Total {formatCOP(Math.round(plan.totalCents / 100))} · la tasa de interés final
                  depende de la entidad bancaria.
                </p>
              </div>
            )}
          </StubPaymentForm>
        </div>

        {pending && (
          <p className="mt-4 text-sm text-fc-muted" role="status">
            {copy.pendingDescription}
          </p>
        )}

        <div className="mt-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(copy.redirectOnNoState)}
            disabled={pending}
          >
            {copy.redirectLabel}
          </Button>
          {orderPoll.data &&
          !isTerminalOrderStatus(orderPoll.data.status) ? null : initiateMut.isError ||
            webhookMut.isError ? (
            <Button variant="secondary" onClick={handleRetry}>
              Volver a empezar
            </Button>
          ) : null}
        </div>
      </Card>
    </section>
  );
}
